import { useEffect, useRef, useState } from "react";
import api, { fmtNZD, fmtDate } from "@/lib/api";
import StageSelect from "@/components/StageSelect";

/**
 * AI plan analysis block for the Materials tab.
 *
 * Fix log (blank-result bug):
 *  - Shows a live stage (uploading → reading pages → analysing → building suggestions)
 *    plus elapsed time, so Luke never sees a silent "Analysing…" that goes nowhere.
 *  - When the LLM finishes with zero items (done_empty) OR fails parsing, surfaces a
 *    clear error with the raw response text, plus a Retry button.
 *  - Lists Previous analyses on the project so Luke can reopen earlier results.
 */
const STAGE_LABEL = {
  queued: "Queued — waiting for the AI…",
  running: "Starting the AI…",
  reading_pages: "Reading plan pages…",
  analysing: "Analysing with AI…",
  building_suggestions: "Building suggestions…",
  done: "Analysis complete.",
  done_empty: "Analysis finished — no items extracted.",
  failed: "Analysis failed.",
};

export default function PlanAnalysisBlock({ projectId, onAdded }) {
  const [progress, setProgress] = useState(0);
  const [job, setJob] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [stages, setStages] = useState([]);
  const [rows, setRows] = useState([]);
  const [addMsg, setAddMsg] = useState("");
  const [addErr, setAddErr] = useState("");
  const [summary, setSummary] = useState([]);
  const [needsTpl, setNeedsTpl] = useState(false);
  const [previous, setPrevious] = useState([]);
  const [lastUploadId, setLastUploadId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiLabourBusy, setAiLabourBusy] = useState(false);
  const [aiLabourResult, setAiLabourResult] = useState(null);
  const fileRef = useRef(null);
  const tickerRef = useRef(null);

  async function reloadStages() {
    try {
      const { data } = await api.get(`/projects/${projectId}/stages`);
      setStages(data || []);
    } catch { /* no-op */ }
  }

  async function loadPrevious() {
    try {
      const { data } = await api.get(`/projects/${projectId}/estimate/plan-analyses`);
      setPrevious(data || []);
    } catch { setPrevious([]); }
  }
  useEffect(() => { loadPrevious(); /* eslint-disable-next-line */ }, [projectId]);

  function startTicker() {
    const t0 = Date.now();
    setElapsed(0);
    clearInterval(tickerRef.current);
    tickerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
  }
  function stopTicker() { clearInterval(tickerRef.current); tickerRef.current = null; }

  async function upload() {
    const f = fileRef.current?.files?.[0]; if (!f) return;
    if (f.size > 50 * 1024 * 1024) { alert("Max 50 MB"); return; }
    setBusy(true); setProgress(0); setRows([]); setSummary([]); setAddMsg(""); setAddErr("");
    setJob({ status: "queued", progress_stage: "queued" }); startTicker();
    try {
      const { data: init } = await api.post("/uploads/init", { filename: f.name, total_size: f.size, mime: f.type });
      const uploadId = init.upload_id; setLastUploadId(uploadId);
      const CHUNK = 2 * 1024 * 1024;
      let offset = 0, index = 0;
      while (offset < f.size) {
        const end = Math.min(offset + CHUNK, f.size);
        const fd = new FormData();
        fd.append("file", f.slice(offset, end), f.name);
        fd.append("chunk_index", index);
        fd.append("is_final", end >= f.size ? "true" : "false");
        await api.post(`/uploads/${uploadId}/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        offset = end; index++; setProgress(Math.round(offset / f.size * 100));
      }
      const { data: jobResp } = await api.post(`/projects/${projectId}/estimate/analyse-plan-job`, { upload_id: uploadId });
      await pollJob(jobResp.job_id);
    } catch (e) {
      setAddErr(e?.response?.data?.detail || "Upload / analysis failed");
      stopTicker();
    } finally { setBusy(false); }
  }

  async function retry() {
    if (!lastUploadId) { alert("No previous upload to retry — upload the file again."); return; }
    setBusy(true); setAddErr(""); setRows([]); setSummary([]);
    setJob({ status: "queued", progress_stage: "queued" }); startTicker();
    try {
      const { data: jobResp } = await api.post(`/projects/${projectId}/estimate/analyse-plan-job`,
                                                { upload_id: lastUploadId });
      await pollJob(jobResp.job_id);
    } catch (e) {
      setAddErr(e?.response?.data?.detail || "Retry failed");
      stopTicker();
    } finally { setBusy(false); }
  }

  async function pollJob(jid) {
    // Poll up to 5 minutes (120 × 2.5s)
    for (let i = 0; i < 120; i++) {
      const { data } = await api.get(`/plan-jobs/${jid}`);
      setJob(data);
      if (["done", "done_empty", "failed"].includes(data.status)) {
        stopTicker();
        if (data.status === "done") await matchItems(data.result || {});
        await loadPrevious();
        return;
      }
      await new Promise(r => setTimeout(r, 2500));
    }
    stopTicker();
    setJob(j => ({ ...(j || {}), status: "failed", error: "Timed out after 5 minutes." }));
  }

  async function matchItems(result) {
    const items = [
      ...(result.materials || []).map(m => ({ kind: "material", desc: m.desc, qty: m.qty, unit: m.unit, trade: m.category || "", rate: m.rate || 0 })),
      ...(result.labour || []).map(l => ({ kind: "labour", task: l.task, hours: l.hours, trade: l.trade, rate: l.rate || 65 })),
      ...(result.subcontractors || []).map(s => ({ kind: "subcontractor", trade: s.trade, scope: s.scope, amount: s.amount || 0 })),
    ];
    if (items.length === 0) {
      setJob(j => ({ ...(j || {}), status: "done_empty",
        error: "The AI didn't extract any items from this plan." }));
      return;
    }
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate/plan-match`, { items });
      if (data.needs_template) { setNeedsTpl(true); setStages([]); setRows([]); return; }
      setNeedsTpl(false); setStages(data.stages || []);
      setRows((data.items || []).map((it, idx) => ({
        ...it, _idx: idx, selected: !!it.suggested_stage_id, stage_id: it.suggested_stage_id || "",
      })));
    } catch (e) { setAddErr(e?.response?.data?.detail || "Could not match items to stages."); }
  }

  function toggle(idx) { setRows(rs => rs.map(r => r._idx === idx ? { ...r, selected: !r.selected } : r)); }
  function toggleAll(c) { setRows(rs => rs.map(r => ({ ...r, selected: c }))); }
  function setStageId(idx, sid) { setRows(rs => rs.map(r => r._idx === idx ? { ...r, stage_id: sid } : r)); }

  const selectedCount = rows.filter(r => r.selected).length;
  const unassignedSelected = rows.filter(r => r.selected && !r.stage_id).length;
  const allSelected = rows.length > 0 && rows.every(r => r.selected);

  async function addSelected() {
    const items = rows.filter(r => r.selected).map(r => ({
      kind: r.kind, desc: r.desc, task: r.task, scope: r.scope, qty: r.qty, hours: r.hours,
      unit: r.unit, rate: r.rate, amount: r.amount, trade: r.trade, stage_id: r.stage_id || "",
    }));
    if (items.length === 0) return;
    const warn = unassignedSelected > 0
      ? `${unassignedSelected} selected item${unassignedSelected===1?"":"s"} have no stage and will go to Unassigned. Continue?`
      : `Add ${items.length} item${items.length===1?"":"s"} to the estimate?`;
    if (!window.confirm(warn)) return;
    setAddMsg(""); setAddErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate/import-ai-plan`,
        { items, analysis_id: job?.id });
      setAddMsg(`Added ${data.added} item${data.added===1?"":"s"} to the estimate ✓`);
      setSummary(data.summary_by_stage || []);
      setRows(rs => rs.filter(r => !r.selected));
      // Auto-fill labour factors right after import so Luke doesn't do it by hand.
      setAiLabourBusy(true);
      try {
        const { data: r } = await api.post(`/projects/${projectId}/estimate/ai-labour-match`,
          { analysis_id: job?.id, only_unassigned: true });
        setAiLabourResult(r);
      } catch (e2) {
        setAiLabourResult({ error: e2?.response?.data?.detail || "AI labour match failed" });
      } finally { setAiLabourBusy(false); }
      if (onAdded) onAdded();
    } catch (e) { setAddErr(e?.response?.data?.detail || "Add failed"); }
  }

  async function manualAutoFill() {
    setAiLabourBusy(true); setAiLabourResult(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate/ai-labour-match`,
        { analysis_id: job?.id, only_unassigned: true });
      setAiLabourResult(data);
      if (onAdded) onAdded();
    } catch (e) {
      setAiLabourResult({ error: e?.response?.data?.detail || "Auto-fill failed" });
    } finally { setAiLabourBusy(false); }
  }
  async function acceptAllAi() {
    try {
      await api.post(`/projects/${projectId}/estimate/ai-labour-match/accept-all`,
        { analysis_id: job?.id });
      setAiLabourResult(r => r ? { ...r, accepted_all: true } : null);
      if (onAdded) onAdded();
    } catch (e) { alert(e?.response?.data?.detail || "Accept failed"); }
  }
  async function undoAiLabour() {
    if (!aiLabourResult?.run_id) return;
    try {
      await api.post(`/projects/${projectId}/estimate/ai-labour-match/${aiLabourResult.run_id}/undo`);
      setAiLabourResult({ ...aiLabourResult, undone: true });
      if (onAdded) onAdded();
    } catch (e) { alert(e?.response?.data?.detail || "Undo failed"); }
  }

  async function deleteAnalysis(j, removeLines) {
    const label = `${fmtDate(j.created_at)} ${j.created_at?.slice(11,16) || ""} (${j.status})`;
    const extra = removeLines ? "\n\nAlso remove the lines this analysis added to the estimate." : "";
    if (!window.confirm(`Delete analysis ${label}?${extra}`)) return;
    try {
      await api.delete(`/projects/${projectId}/estimate/plan-analyses/${j.id}` +
                       (removeLines ? "?remove_lines=true" : ""));
      await loadPrevious();
      if (onAdded) onAdded();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  }
  async function deleteAllOld() {
    if (!window.confirm("Delete all old analyses on this project? The newest is kept. (Restorable.)")) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate/plan-analyses/delete-all-old`, {});
      await loadPrevious();
      alert(`Deleted ${data.deleted} old analysis / analyses. Newest kept.`);
    } catch (e) { alert(e?.response?.data?.detail || "Delete-all failed"); }
  }

  async function reopenPrevious(j) {
    setJob(j);
    if (j.status === "done" && j.result) await matchItems(j.result);
    else if (j.status === "done_empty") setRows([]);
  }

  const stageLbl = STAGE_LABEL[job?.progress_stage || job?.status] || job?.status || "";
  const showError = job && (job.status === "failed" || job.status === "done_empty");
  const canRetry = !!lastUploadId && !busy;

  return (
    <div className="card" data-testid="ai-plan-block">
      <h3 className="text-silver mb-2">Analyse plans (AI)</h3>
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept="application/pdf" className="text-white text-sm" data-testid="ai-plan-file"/>
        <button className="btn-silver" onClick={upload} disabled={busy} data-testid="btn-analyse-plan">
          {busy ? "Working…" : "Analyse plans (AI)"}
        </button>
        {canRetry && <button className="btn-ghost text-sm" onClick={retry} data-testid="btn-retry-analysis">Retry</button>}
      </div>

      {progress > 0 && progress < 100 && (
        <div className="mt-2 text-sm">
          <div className="h-2 bg-neutral-800 rounded overflow-hidden"><div className="h-2 bg-silver" style={{ width: `${progress}%` }}/></div>
          <div className="opacity-70 mt-1">{progress}% uploaded</div>
        </div>
      )}

      {job && job.status !== "done" && (
        <div className="mt-3 text-sm text-silver flex items-center gap-3" data-testid="ai-progress">
          <span>{stageLbl}</span>
          {!["done", "done_empty", "failed"].includes(job.status) && (
            <span className="opacity-60 text-xs">elapsed {elapsed}s</span>
          )}
        </div>
      )}

      {showError && (
        <div className="mt-3 border border-amber-500/40 bg-amber-500/10 rounded p-3 text-sm" data-testid="ai-error-card">
          <div className="text-amber-200 mb-1">
            {job.status === "done_empty" ? "No items extracted" : "Analysis failed"}
            {typeof job.elapsed_seconds === "number" && <span className="opacity-60 ml-2">({job.elapsed_seconds}s)</span>}
          </div>
          <div className="opacity-80 whitespace-pre-wrap break-words">{job.error || "Unknown error."}</div>
          {job.raw_response && (
            <details className="mt-2">
              <summary className="cursor-pointer opacity-70 text-xs">Show raw AI response</summary>
              <pre className="text-xs opacity-70 whitespace-pre-wrap break-words mt-1 max-h-56 overflow-auto">{job.raw_response}</pre>
            </details>
          )}
          {canRetry && (
            <button className="btn-silver text-sm mt-3" onClick={retry} data-testid="btn-retry-after-fail">
              Retry analysis
            </button>
          )}
        </div>
      )}

      {needsTpl && (
        <div className="mt-3 border border-amber-500/50 bg-amber-500/10 p-2 rounded text-sm text-amber-200" data-testid="ai-needs-template">
          This project has no stages yet. Open the <b>Tasks</b> tab and apply a build template first — the AI needs stages to map each suggestion to.
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="ai-suggestions">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm flex items-center gap-1">
              <input type="checkbox" checked={allSelected} onChange={e=>toggleAll(e.target.checked)} data-testid="ai-select-all"/>
              Select all
            </label>
            <button className="btn-silver text-sm" disabled={selectedCount === 0} onClick={addSelected} data-testid="btn-add-selected">
              Add selected to estimate ({selectedCount})
            </button>
            {unassignedSelected > 0 && (
              <span className="badge badge-amber" data-testid="ai-unassigned-warn">
                {unassignedSelected} unmatched — will go to Unassigned
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="table min-w-[720px] md:min-w-full">
              <thead><tr>
                <th></th><th>Kind</th><th>Description</th><th>Qty / Hours</th><th>Rate</th><th>Stage</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r._idx} data-testid={`ai-row-${r._idx}`}>
                    <td><input type="checkbox" checked={!!r.selected} onChange={()=>toggle(r._idx)} data-testid={`ai-select-${r._idx}`}/></td>
                    <td className="opacity-70 text-xs">{r.kind}</td>
                    <td>{r.desc || r.task || r.scope || "—"}{r.trade ? <span className="opacity-50 text-xs"> · {r.trade}</span> : null}</td>
                    <td>{r.kind === "labour" ? `${r.hours || 0}h` : (r.kind === "material" ? `${r.qty || 0} ${r.unit || ""}` : "—")}</td>
                    <td>{r.kind === "subcontractor" ? fmtNZD(r.amount || 0) : fmtNZD(r.rate || 0)}</td>
                    <td>
                      <StageSelect projectId={projectId} value={r.stage_id} stages={stages}
                        onStagesChanged={reloadStages}
                        onChange={(sid)=>setStageId(r._idx, sid || "")}
                        data-testid={`ai-stage-${r._idx}`}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {addMsg && <div className="text-green-400 text-sm" data-testid="ai-add-msg">{addMsg}</div>}
          {addErr && <div className="text-red-500 text-sm" data-testid="ai-add-err">{addErr}</div>}
        </div>
      )}

      {summary.length > 0 && (
        <div className="mt-3 card" data-testid="ai-summary">
          <div className="text-silver text-sm mb-2">What was added, by stage</div>
          <table className="table">
            <thead><tr><th>Stage</th><th>Labour hrs</th><th>Labour $</th><th>Materials $</th><th>Subbies $</th></tr></thead>
            <tbody>
              {summary.map(s => (
                <tr key={s.stage_id || "unassigned"} data-testid={`ai-sum-${s.stage_id || "unassigned"}`}
                    className={!s.stage_id ? "bg-amber-500/10" : ""}>
                  <td className={!s.stage_id ? "text-amber-300" : "text-silver"}>{s.stage_name}</td>
                  <td>{s.labour_hours}</td>
                  <td>{fmtNZD(s.labour_dollars)}</td>
                  <td>{fmtNZD(s.materials_dollars)}</td>
                  <td>{fmtNZD(s.subs_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AI labour auto-fill results */}
      {(aiLabourBusy || aiLabourResult) && (
        <div className="mt-3 card" data-testid="ai-labour-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-silver text-sm">Auto-fill labour factors (AI)</div>
            <div className="flex gap-2 items-center">
              <button className="btn-ghost text-xs" disabled={aiLabourBusy || !job}
                      onClick={manualAutoFill} data-testid="btn-ai-labour-run">
                {aiLabourBusy ? "Running…" : "Auto-fill labour factors"}
              </button>
              {aiLabourResult?.run_id && !aiLabourResult.undone && (
                <>
                  <button className="btn-ghost text-xs" onClick={acceptAllAi} data-testid="btn-ai-labour-accept">
                    Accept all AI suggestions
                  </button>
                  <button className="btn-ghost text-xs" onClick={undoAiLabour} data-testid="btn-ai-labour-undo">
                    Undo this auto-fill
                  </button>
                </>
              )}
            </div>
          </div>
          {aiLabourBusy && <div className="text-xs opacity-70 mt-1">AI is matching labour factors…</div>}
          {aiLabourResult?.error && <div className="text-red-400 text-xs mt-1">{aiLabourResult.error}</div>}
          {aiLabourResult?.run_id && (
            <div className="text-xs mt-2 space-y-1" data-testid="ai-labour-result">
              <div>Model calls: <b className="text-silver">{aiLabourResult.model_calls}</b> · unique descriptions: <b className="text-silver">{aiLabourResult.unique_descs}</b> · assigned: <b className="text-silver">{aiLabourResult.assigned.length}</b> · exceptions: <b className="text-amber-300">{aiLabourResult.exceptions.length}</b>{aiLabourResult.accepted_all ? " · accepted ✓" : ""}{aiLabourResult.undone ? " · undone" : ""}</div>
              {aiLabourResult.exceptions.length > 0 && (
                <div data-testid="ai-labour-exceptions">
                  <div className="text-amber-300">⚠ No match: check — {aiLabourResult.exceptions.length} line{aiLabourResult.exceptions.length===1?"":"s"}</div>
                  <ul className="list-disc ml-5 opacity-80 max-h-32 overflow-y-auto">
                    {aiLabourResult.exceptions.slice(0, 20).map((e, i) => (
                      <li key={i} className="text-amber-200">{e.desc} — {e.reason}{typeof e.confidence === "number" ? ` (conf ${e.confidence.toFixed(2)})` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {previous.length > 0 && (
        <div className="mt-4" data-testid="ai-previous">
          <div className="flex items-center justify-between">
            <div className="text-silver text-sm mb-1">Previous analyses</div>
            {previous.length > 1 && (
              <button className="btn-ghost text-xs" onClick={deleteAllOld} data-testid="btn-ai-delete-old">
                Delete all old (keep newest)
              </button>
            )}
          </div>
          <ul className="text-xs space-y-0.5">
            {previous.slice(0, 8).map(j => (
              <li key={j.id} className="flex items-center gap-2" data-testid={`ai-prev-${j.id}`}>
                <span className="opacity-60">{j.created_at ? fmtDate(j.created_at) + " " + (j.created_at.slice(11,16)) : "—"}</span>
                <span className={j.status === "done" ? "text-silver" :
                                 j.status === "done_empty" ? "text-amber-300" :
                                 j.status === "failed" ? "text-red-400" : "opacity-70"}>
                  {j.status}
                </span>
                {typeof j.elapsed_seconds === "number" && <span className="opacity-60">· {j.elapsed_seconds}s</span>}
                <button className="hub-link text-xs" onClick={() => reopenPrevious(j)}
                        data-testid={`ai-reopen-${j.id}`}>Reopen</button>
                <button className="hub-link text-xs text-red-400" onClick={() => deleteAnalysis(j, false)}
                        data-testid={`btn-ai-del-${j.id}`}>Delete</button>
                <button className="hub-link text-xs text-red-400" onClick={() => deleteAnalysis(j, true)}
                        data-testid={`btn-ai-del-lines-${j.id}`}>Delete + lines</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
